import { useState } from "react";
import { Code, Download, Printer } from "lucide-react";
import { Button } from "../../components/Button";
import { emlUrl, mailbox, mailErrorMessage } from "../../api/mailbox";
import type { MailScope } from "../../api/mailbox";

// 讀信的「列印 / 顯示原始碼 / 下載 .eml」(規格 §2.6)。
//
// - 列印:`window.print()`;頁面的列印樣式(見 MailboxPage 的 PRINT_CSS)只印讀信區。
// - 原始碼:完整標頭與原文,放 `<pre>` 當**純文字**(React 的文字節點,不會變成 HTML)。
// - .eml:備份的原始檔,一個位元組都不改。

export function MailSourceTools({ messageId, scope = "messages" }: { messageId: number; scope?: MailScope }) {
  const [source, setSource] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const toggleSource = async () => {
    if (source !== null) {
      setSource(null);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const got = await mailbox.source(messageId, scope);
      setSource(typeof got === "string" ? got : JSON.stringify(got));
    } catch (err) {
      setError(mailErrorMessage(err, "讀不到原始碼,請稍後再試"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex flex-col gap-2 print:hidden">
      <div className="flex flex-wrap gap-2">
        <Button size="sm" variant="ghost" icon={<Printer aria-hidden="true" size={16} />} onClick={() => window.print()}>
          列印
        </Button>
        <Button
          size="sm"
          variant="ghost"
          aria-expanded={source !== null}
          loading={loading}
          icon={<Code aria-hidden="true" size={16} />}
          onClick={() => void toggleSource()}
        >
          {source !== null ? "隱藏原始碼" : "顯示原始碼"}
        </Button>
        <a
          href={emlUrl(messageId, scope)}
          download
          className="inline-flex h-8 items-center gap-1.5 rounded-[var(--radius)] px-2.5 text-sm text-[var(--text-2)] hover:bg-[var(--surface-2)]"
        >
          <Download aria-hidden="true" size={16} />
          下載 .eml
        </a>
      </div>
      {error ? (
        <p role="alert" className="m-0 text-sm text-[var(--danger)]">
          {error}
        </p>
      ) : null}
      {source !== null ? (
        <pre
          aria-label="原始碼"
          tabIndex={0}
          className="m-0 max-h-[60vh] overflow-auto whitespace-pre-wrap break-all rounded-[var(--radius)] border border-[var(--border)] bg-[var(--surface-2)] p-3 font-mono text-xs text-[var(--text)]"
        >
          {source}
        </pre>
      ) : null}
    </div>
  );
}
